import { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, API, apiError } from "@/lib/api";
import { resizeImage } from "@/lib/image";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Images, Upload, Trash2, ArrowUp, ArrowDown, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const FotoCard = ({ f, idx, total, refresh }) => {
  const [cap, setCap] = useState(f.didascalia || "");
  const patch = async (body) => {
    try { await api.patch(`/galleria/${f.id}`, body); refresh(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const move = async (dir) => { await api.post(`/galleria/${f.id}/move`, { dir }); refresh(); };
  const remove = async () => {
    if (!window.confirm("Eliminare questa foto?")) { return; }
    await api.delete(`/galleria/${f.id}`); toast.success("Foto eliminata"); refresh();
  };
  return (
    <div data-testid={`galleria-item-${idx}`} className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      <img src={`${API}/public/galleria/${f.id}/image`} alt={f.didascalia || "Foto"} className="aspect-[4/3] w-full object-cover" />
      <div className="space-y-3 p-3">
        <Input value={cap} onChange={(e) => setCap(e.target.value)} onBlur={() => { if (cap !== (f.didascalia || "")) { patch({ didascalia: cap }); } }}
          placeholder="Didascalia (facoltativa)" data-testid={`galleria-caption-${idx}`} />
        <div className="flex items-center justify-between gap-2">
          <label className="flex items-center gap-2 text-xs text-slate-600">
            <Switch checked={f.home} onCheckedChange={(v) => patch({ home: v })} data-testid={`galleria-home-toggle-${idx}`} /> In Home
          </label>
          <div className="flex gap-1">
            <Button size="icon" variant="outline" className="h-8 w-8" disabled={idx === 0} onClick={() => move(-1)} data-testid={`galleria-up-${idx}`}><ArrowUp className="h-4 w-4" /></Button>
            <Button size="icon" variant="outline" className="h-8 w-8" disabled={idx === total - 1} onClick={() => move(1)} data-testid={`galleria-down-${idx}`}><ArrowDown className="h-4 w-4" /></Button>
            <Button size="icon" variant="outline" className="h-8 w-8 text-red-700" onClick={remove} data-testid={`galleria-delete-${idx}`}><Trash2 className="h-4 w-4" /></Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function Galleria() {
  const { user } = useAuth();
  if (!["admin", "coordinatore"].includes(user?.role)) {
    return <p data-testid="galleria-forbidden" className="text-sm text-slate-500">Sezione riservata ad amministratori e coordinatori.</p>;
  }
  return <GalleriaManager />;
}

function GalleriaManager() {
  const qc = useQueryClient();
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const { data: foto = [], isLoading } = useQuery({ queryKey: ["galleria"], queryFn: async () => (await api.get("/public/galleria")).data });
  const refresh = () => qc.invalidateQueries({ queryKey: ["galleria"] });

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) { return; }
    setBusy(true);
    try {
      for (const file of files) {
        const image = await resizeImage(file, 1600, 1600, 0.82);
        await api.post("/galleria", { image, didascalia: "", home: true });
      }
      toast.success(files.length > 1 ? `${files.length} foto caricate` : "Foto caricata");
      refresh();
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Caricamento non riuscito"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="galleria-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 font-heading text-3xl font-bold text-slate-900"><Images className="h-7 w-7 text-[#166534]" /> Galleria foto</h1>
          <p className="mt-1 text-sm text-slate-500">Le foto con "In Home" scorrono nella pagina di accesso; tutte compaiono nella galleria del Sito ufficiale.</p>
        </div>
        <input ref={inputRef} type="file" accept="image/*" multiple className="hidden" onChange={upload} data-testid="galleria-file-input" />
        <Button onClick={() => inputRef.current?.click()} disabled={busy} className="bg-[#14382C] hover:bg-[#0E281F]" data-testid="galleria-upload-button">
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />} Carica foto
        </Button>
      </div>
      {isLoading ? <Loader2 className="h-6 w-6 animate-spin text-slate-400" /> : foto.length === 0 ? (
        <p data-testid="galleria-empty" className="rounded-xl border border-dashed border-slate-300 p-10 text-center text-sm text-slate-500">Nessuna foto caricata. Finché non ne carichi, la Home e il Sito ufficiale mostrano le foto predefinite.</p>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {foto.map((f, idx) => <FotoCard key={f.id} f={f} idx={idx} total={foto.length} refresh={refresh} />)}
        </div>
      )}
    </div>
  );
}
